import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useMatch } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { MessageSquare, X, Send, ChevronDown } from "lucide-react";

const roleColor = { admin: "#1B1E23", pm: "#0E62C4", designer: "#BE2E6C" };

function timeOf(iso) {
  try { return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function FloatingChatWidget() {
  const { user } = useAuth();
  const t = useT();
  const location = useLocation();
  const projMatch = useMatch("/projekte/:id");
  const routeProjektId = projMatch?.params?.id || null;

  const [open, setOpen] = useState(false);
  const [projekte, setProjekte] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState([]); // list of unread message objects
  const [pickerOpen, setPickerOpen] = useState(false);
  const scrollRef = useRef(null);

  // Hide widget on login/invite pages or when not authed
  const hideOnPath = /^\/(login|invite\/|kundenfreigabe\/)/.test(location.pathname);
  const visible = !!user && !hideOnPath;

  // Load accessible projects once open
  const loadProjekte = async () => {
    try {
      const { data } = await api.get("/projekte");
      const active = (data || []).filter((p) => !p.archiviert);
      setProjekte(active);
      // pre-select: route project > first unread's project > first active
      if (!selectedId) {
        const unreadProjId = unread[0]?.projekt_id;
        setSelectedId(routeProjektId || unreadProjId || active[0]?.id || null);
      }
    } catch (e) { /* silent */ }
  };

  // Unread poll (works even when closed)
  const loadUnread = async () => {
    try {
      const { data } = await api.get("/chat/unread");
      setUnread(data || []);
    } catch { setUnread([]); }
  };

  useEffect(() => {
    if (!visible) return;
    loadUnread();
    const t = setInterval(loadUnread, 25000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [visible]);

  // When opened, load projects
  useEffect(() => {
    if (open && visible) loadProjekte();
    // eslint-disable-next-line
  }, [open, visible]);

  // Sync route project when navigating
  useEffect(() => {
    if (routeProjektId) setSelectedId(routeProjektId);
    // eslint-disable-next-line
  }, [routeProjektId]);

  // Load messages when project changes
  const loadMessages = async () => {
    if (!selectedId) { setMessages([]); return; }
    try {
      const { data } = await api.get(`/projekte/${selectedId}/chat`);
      setMessages(data || []);
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
      // refresh unread after read
      loadUnread();
    } catch (e) {
      setMessages([]);
    }
  };

  useEffect(() => {
    if (open && selectedId) {
      loadMessages();
      const t = setInterval(loadMessages, 20000);
      return () => clearInterval(t);
    }
    // eslint-disable-next-line
  }, [open, selectedId]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || !selectedId) return;
    setBusy(true);
    try {
      await api.post(`/projekte/${selectedId}/chat`, { text: t });
      setText("");
      await loadMessages();
    } finally { setBusy(false); }
  };

  const grouped = useMemo(() => {
    const out = []; let lastDate = null;
    (messages || []).forEach((m) => {
      const d = fmtDate(m.erstellt_am);
      if (d !== lastDate) { out.push({ divider: d, id: `d-${d}` }); lastDate = d; }
      out.push(m);
    });
    return out;
  }, [messages]);

  const unreadCount = unread.length;
  const unreadByProject = useMemo(() => {
    const m = new Map();
    unread.forEach((u) => m.set(u.projekt_id, (m.get(u.projekt_id) || 0) + 1));
    return m;
  }, [unread]);

  const selected = projekte.find((p) => p.id === selectedId) || null;

  if (!visible) return null;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="floating-chat-btn"
          aria-label={t("chat.title")}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-ink text-white shadow-lg hover:bg-black transition-colors flex items-center justify-center"
        >
          <MessageSquare size={22} strokeWidth={1.8} />
          {unreadCount > 0 && (
            <span
              data-testid="floating-chat-unread"
              className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#BE2E6C] text-white text-[11px] font-bold flex items-center justify-center border-2 border-white"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Popup */}
      {open && (
        <div
          data-testid="floating-chat-panel"
          className="fixed bottom-6 right-6 z-40 w-[360px] h-[540px] bg-white border border-line shadow-2xl rounded-sm flex flex-col overflow-hidden"
          style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.18)" }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-line bg-paper/70 flex items-center justify-between">
            <div>
              <h3 className="font-archivo font-bold text-sm">{t("chat.title")}</h3>
              <div className="mono-label mt-0.5" style={{ opacity: 1 }}>
                {unreadCount > 0
                  ? (unreadCount === 1 ? t("chat.unread_one") : t("chat.unread_count").replace("{n}", unreadCount))
                  : t("chat.saved_hint")}
              </div>
            </div>
            <button
              onClick={() => { setOpen(false); setPickerOpen(false); }}
              data-testid="floating-chat-close"
              className="p-1.5 rounded-sm hover:bg-line/50"
              aria-label={t("common.close")}
            >
              <X size={16} />
            </button>
          </div>

          {/* Project picker */}
          <div className="px-3 py-2 border-b border-line relative">
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              data-testid="floating-chat-project-toggle"
              className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-line rounded-sm text-sm bg-white hover:border-ink"
            >
              <span className="truncate text-left">
                {selected ? (
                  <>
                    <span className="font-medium">{selected.titel}</span>
                    <span className="mono-label ml-2" style={{ opacity: 1 }}>{selected.kunde_name}</span>
                  </>
                ) : projekte.length === 0 ? (
                  <span className="text-ink/50">{t("chat.no_projects")}</span>
                ) : (
                  <span className="text-ink/50">{t("chat.pick_project")}</span>
                )}
              </span>
              <ChevronDown size={14} className={`shrink-0 transition-transform ${pickerOpen ? "rotate-180" : ""}`} />
            </button>
            {pickerOpen && (
              <div
                data-testid="floating-chat-project-list"
                className="absolute left-3 right-3 top-full mt-1 max-h-64 overflow-y-auto bg-white border border-line rounded-sm shadow-lg z-10"
              >
                {projekte.length === 0 && (
                  <div className="px-3 py-3 text-sm text-ink/50">{t("chat.no_active_projects")}</div>
                )}
                {projekte.map((p) => {
                  const badge = unreadByProject.get(p.id) || 0;
                  const active = p.id === selectedId;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => { setSelectedId(p.id); setPickerOpen(false); }}
                      data-testid={`floating-chat-project-${p.id}`}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-paper flex items-center justify-between gap-2 ${active ? "bg-paper" : ""}`}
                    >
                      <span className="min-w-0">
                        <span className="block font-medium truncate">{p.titel}</span>
                        <span className="mono-label" style={{ opacity: 1 }}>{p.kunde_name}</span>
                      </span>
                      {badge > 0 && (
                        <span className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-[#BE2E6C] text-white text-[10px] font-bold flex items-center justify-center">
                          {badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="floating-chat-messages">
            {!selectedId && (
              <div className="h-full flex items-center justify-center text-sm text-ink/40 text-center px-6">
                {t("chat.select_project_hint")}
              </div>
            )}
            {selectedId && grouped.length === 0 && (
              <div className="h-full flex items-center justify-center text-sm text-ink/40">{t("chat.no_messages")}</div>
            )}
            {grouped.map((m) => {
              if (m.divider) return (
                <div key={m.id} className="text-center my-2">
                  <span className="mono-label px-2" style={{ opacity: 1 }}>{m.divider}</span>
                </div>
              );
              const mine = m.verfasser_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`floating-chat-msg-${m.id}`}>
                  <div className="max-w-[80%]">
                    {!mine && (
                      <div className="mono-label mb-0.5 pl-1" style={{ color: roleColor[m.verfasser_rolle] || "#1B1E23", opacity: 1 }}>
                        {m.verfasser_name} · {m.verfasser_rolle}
                      </div>
                    )}
                    <div
                      className={`rounded-md px-3 py-2 text-sm whitespace-pre-wrap break-words ${mine ? "text-white" : "bg-paper border border-line"}`}
                      style={mine ? { backgroundColor: roleColor[m.verfasser_rolle] || "#1B1E23" } : {}}
                    >
                      {m.text}
                    </div>
                    <div className={`mono-label mt-0.5 ${mine ? "text-right pr-1" : "pl-1"}`}>{timeOf(m.erstellt_am)}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Input */}
          <form onSubmit={send} className="p-2.5 border-t border-line flex gap-2" data-testid="floating-chat-form">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="floating-chat-input"
              placeholder={selectedId ? t("chat.input_placeholder") : t("chat.input_placeholder_no_project")}
              disabled={!selectedId}
              className="flex-1 px-3 py-2 border border-line rounded-sm text-sm bg-white focus:outline-none focus:border-ink disabled:bg-paper/50 disabled:text-ink/40"
            />
            <button
              type="submit"
              disabled={busy || !text.trim() || !selectedId}
              data-testid="floating-chat-send"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-ink text-white rounded-sm text-sm hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Send size={13} /> {t("chat.send")}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
